import React, { useEffect, useState, useMemo } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { Select, Skeleton } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { SeriesBook } from './types';
import './SeriesNavigator.css';

const { Option } = Select;

const API_BASE = resolveFrontendApiBase();

interface SeriesNavigatorProps {
  seriesName: string;
  seriesId?: string | null;
  currentBookId: string;
  currentBookNumber: number;
  userId: string;
}

const SeriesNavigator: React.FC<SeriesNavigatorProps> = ({
  seriesName,
  seriesId,
  currentBookId,
  userId,
}) => {
  const [books, setBooks] = useState<SeriesBook[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!seriesName && !seriesId) return;
    let canceled = false;
    const load = async () => {
      try {
        setLoading(true);
        const merged: Record<string, SeriesBook> = {};

        // Fetch by id first (if available)
        if (seriesId) {
          try {
            const resp = await axios.get<SeriesBook[]>(
              `${API_BASE}/series/${seriesId}/books?user_id=${userId}`
            );
            (resp.data || []).forEach(item => {
              merged[item.book_guid] = item;
            });
          } catch {
            // ignore and continue to name-based lookup
          }
        }

        // Always also fetch by name to include un-attached books with the same series label
        if (seriesName) {
          try {
            const resp = await axios.get<SeriesBook[]>(
              `${API_BASE}/uploadedbooks/series/${encodeURIComponent(seriesName)}?user_id=${userId}`
            );
            (resp.data || []).forEach(item => {
              merged[item.book_guid] = item;
            });
          } catch {
            // ignore
          }
        }

        const combined = Object.values(merged).sort((a, b) => {
          const numA = a.booknumber ?? 0;
          const numB = b.booknumber ?? 0;
          if (numA !== numB) return numA - numB;
          return (a.title || '').localeCompare(b.title || '');
        });

        if (!canceled) setBooks(combined);
      } catch (err) {
        // silent fail, no nav if error
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    load();
    return () => {
      canceled = true;
    };
  }, [seriesName, seriesId, userId]);

  const idx = useMemo(
    () => books.findIndex(b => b.book_guid === currentBookId),
    [books, currentBookId]
  );

  if ((!seriesName && !seriesId) || books.length <= 1) {
    return null; // nothing to navigate
  }

  if (loading) {
    return (
      <div className="series-navigator series-navigator--loading" aria-label="Loading series navigation">
        <Skeleton active paragraph={false} title={{ width: 260 }} />
      </div>
    );
  }

  const handleJump = (bookId: string) => {
    navigate(`/book-detail/${bookId}`);
  };

  return (
    <Select
      aria-label="Choose book in series"
      className="series-navigator"
      value={currentBookId}
      onChange={handleJump}
      optionLabelProp="label"
      showSearch
      filterOption={(input, option) =>
        (option?.label as string).toLowerCase().includes(input.toLowerCase())
      }
    >
      {books.map(b => (
        <Option
          key={b.book_guid}
          value={b.book_guid}
          label={`${b.booknumber ? `#${b.booknumber} ` : ''}${b.title}`}
        >
          {b.booknumber ? `#${b.booknumber}` : '#?'} {b.title}
        </Option>
      ))}
    </Select>
  );
};

export default SeriesNavigator;
